import React, { useState } from 'react';

const ReportPage = ({ userInfo, onReturnToLanding }) => {
  const [step, setStep] = useState('contact'); // 'contact', 'generating', 'viewing'
  const [wantsContact, setWantsContact] = useState(null);
  const [contactMethod, setContactMethod] = useState('');
  const [contactValue, setContactValue] = useState('');
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const validatePhone = (phone) => {
    // Basic phone validation: should contain country code, area code, and number
    const phoneRegex = /^\+\d{1,3}\s?\d{3,4}\s?\d{3,4}\s?\d{4,}$/;
    return phoneRegex.test(phone.replace(/[\s-()]/g, ''));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleContactSubmit = async () => {
    setError('');
    
    let contactInfo = null;
    
    if (wantsContact === 'yes') {
      if (!contactMethod) {
        setError('Please select a contact method');
        return;
      }
      
      if (!contactValue.trim()) {
        setError(`Please enter your ${contactMethod}`);
        return;
      }
      
      if (contactMethod === 'phone' && !validatePhone(contactValue)) {
        setError('Please enter a valid phone number with country code (e.g., +1 555 123 4567)');
        return;
      }
      
      if (contactMethod === 'email' && !validateEmail(contactValue)) {
        setError('Please enter a valid email address');
        return;
      }
      
      contactInfo = {
        [contactMethod]: contactValue
      };
    }
    
    // Generate report
    setStep('generating');
    setLoading(true);
    
    try {
      const response = await fetch('/api/generate-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userInfo,
          contactInfo
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setReportData(result);
        setStep('viewing');
      } else {
        setError(`Failed to generate report: ${result.error}`);
        setStep('contact');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report. Please try again.');
      setStep('contact');
    }
    
    setLoading(false);
  };

  const handleDownload = async () => {
    if (reportData && reportData.reportFileName) {
      try {
        const response = await fetch(`/api/download-report/${reportData.reportFileName}`);
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = reportData.reportFileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          setError('Failed to download report. Please try again.');
        }
      } catch (err) {
        console.error('Download error:', err);
        setError('Failed to download report. Please try again.');
      }
    }
  };

  if (step === 'contact') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 w-full max-w-lg">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="/logos/DP LOGO - ON WHT.png" 
              alt="Dewpoint Group Logo" 
              className="h-16 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold gradient-text mb-4">
              Assessment Complete!
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for completing the AI Maturity Assessment. 
              We'll now generate your personalized report.
            </p>
          </div>

          {/* Contact Preference */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Would you like to be contacted about your results?
            </h3>
            
            <div className="space-y-3 mb-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="contact"
                  value="yes"
                  checked={wantsContact === 'yes'}
                  onChange={(e) => setWantsContact(e.target.value)}
                  className="mr-3"
                />
                <span>Yes, I'd like to be contacted</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="radio"
                  name="contact"
                  value="no"
                  checked={wantsContact === 'no'}
                  onChange={(e) => setWantsContact(e.target.value)}
                  className="mr-3"
                />
                <span>No, just generate my report</span>
              </label>
            </div>

            {/* Contact Method Selection */}
            {wantsContact === 'yes' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Contact Method
                  </label>
                  <select
                    value={contactMethod}
                    onChange={(e) => setContactMethod(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select contact method</option>
                    <option value="phone">Phone</option>
                    <option value="email">Email</option>
                  </select>
                </div>

                {contactMethod === 'phone' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number (with country code)
                    </label>
                    <input
                      type="tel"
                      value={contactValue}
                      onChange={(e) => setContactValue(e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="input-field"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Include country code and area code
                    </p>
                  </div>
                )}

                {contactMethod === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={contactValue}
                      onChange={(e) => setContactValue(e.target.value)}
                      placeholder="your.email@company.com"
                      className="input-field"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              onClick={onReturnToLanding}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleContactSubmit}
              disabled={wantsContact === null || loading}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'generating') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 w-full max-w-lg text-center">
          <div className="flex justify-center mb-8">
            <img 
              src="/logos/DP LOGO - ON WHT.png" 
              alt="Dewpoint Group Logo" 
              className="h-16 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          <div className="mb-8">
            {/* Enhanced Animation */}
            <div className="relative mb-8">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-pulse text-2xl">🤖</div>
              </div>
            </div>
            
            {/* Progress Dots Animation */}
            <div className="flex justify-center space-x-2 mb-6">
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Generating Your AI Maturity Report
            </h2>
            <p className="text-gray-600 mb-6">
              Our AI-powered analysis is creating your personalized assessment report. 
              This may take a few moments...
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-6">
            <p className="text-sm text-blue-800">
              <strong>What's happening:</strong><br/>
              • Analyzing your responses<br/>
              • Calculating maturity scores<br/>
              • Generating insights and recommendations<br/>
              • Creating your PDF report
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'viewing') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 w-full max-w-2xl text-center">
          <div className="flex justify-center mb-8">
            <img 
              src="/logos/DP LOGO - ON WHT.png" 
              alt="Dewpoint Group Logo" 
              className="h-16 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          <div className="mb-8">
            <div className="text-6xl mb-6">✅</div>
            <h1 className="text-3xl font-bold gradient-text mb-4">
              Your AI Maturity Report is Ready!
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Company: <strong>{userInfo?.companyName}</strong> | 
              Participant: <strong>{userInfo?.userName}</strong>
            </p>
            <p className="text-gray-600 mb-8">
              Your personalized AI maturity report includes detailed analysis, recommendations, 
              and a strategic roadmap for advancing your organization's AI capabilities.
            </p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleDownload}
              className="w-full btn-primary text-lg py-4"
            >
              📥 Download your AI Maturity Report - PDF
            </button>
            
            <button
              onClick={onReturnToLanding}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Take Another Assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ReportPage;